import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { Button } from '@components/ui';
import { LogoUpload } from '@components/ui/LogoUpload/LogoUpload';
import { useCompanyQuery } from '@dieterApi/company/useCompanyQuery';
import { useWhitelabelConfigUpsertMutation } from '@dieterApi/whitelabel/useWhitelabelConfigUpsertMutation';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import './whitelabel-configurator.scss';

export const WhitelabelConfigForm = () => {
  const { t } = useTranslation();
  const { user } = useUserWithCompany();
  const { data: principalCompanyData, loading: principalCompanyLoading } = useCompanyQuery(
    user?.companies.find((c) => c.isPrincipal)?.id || ''
  );
  const principalCompany = principalCompanyData?.getCompany;

  const [hasWhitelabelPerms, setHasWhitelabelPerms] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const [primaryColor, setPrimaryColor] = useState('#ffffff');
  const [secondaryColor, setSecondaryColor] = useState('#000000');
  const [successColor, setSuccessColor] = useState('#91ab46');
  const [warningColor, setWarningColor] = useState('#ffc107');
  const [dangerColor, setDangerColor] = useState('#ec474f');
  const [domain, setDomain] = useState('');
  const [privacyUrl, setPrivacyUrl] = useState('');
  const [emailFooter, setEmailFooter] = useState('');
  const [avatarName, setAvatarName] = useState('');
  const [klausName, setKlausName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [logoB64, setLogoB64] = useState<string | null>(null);
  const [lightLogoB64, setLightLogoB64] = useState<string | null>(null);
  const [avatarB64, setAvatarB64] = useState<string | null>(null);

  useEffect(() => {
    if (principalCompany) {
      if (principalCompany?.features?.includes('WhitelabelAdmin')) {
        setHasWhitelabelPerms(true);
        setPrimaryColor(principalCompany.whitelabelConfig?.primaryColor || '#ab5fce');
        setSecondaryColor(principalCompany.whitelabelConfig?.secondaryColor || '#fdd213');
        setSuccessColor(principalCompany.whitelabelConfig?.successColor || '#91ab46');
        setWarningColor(principalCompany.whitelabelConfig?.warningColor || '#ffc107');
        setDangerColor(principalCompany.whitelabelConfig?.dangerColor || '#ec474f');
        setDomain(principalCompany.whitelabelConfig?.domain || '');
        setPrivacyUrl(principalCompany.whitelabelConfig?.privacyUrl || '');
        setEmailFooter(principalCompany.whitelabelConfig?.emailFooter || '');
        setAvatarName(principalCompany.whitelabelConfig?.avatarName || '');
        setKlausName(principalCompany.whitelabelConfig?.klausName || '');
        setSenderEmail(principalCompany.whitelabelConfig?.senderEmail || '');
        setSenderName(principalCompany.whitelabelConfig?.senderName || '');
        setSupportEmail(principalCompany.whitelabelConfig?.supportEmail || '');
        setLogoB64(principalCompany.whitelabelConfig?.logoB64 || null);
        setLightLogoB64(principalCompany.whitelabelConfig?.lightLogoB64 || null);
        setAvatarB64(principalCompany.whitelabelConfig?.avatarB64 || null);
      } else {
        setHasWhitelabelPerms(false);
      }
    }
  }, [principalCompany]);

  const [upsertWhitelabelConfig, { loading, error }] = useWhitelabelConfigUpsertMutation();

  const handlePrimaryColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrimaryColor(e.target.value);
  };

  const handleSecondaryColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSecondaryColor(e.target.value);
  };
  const handleSuccessColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSuccessColor(e.target.value);
  };
  const handleWarningColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWarningColor(e.target.value);
  };
  const handleDangerColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDangerColor(e.target.value);
  };
  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDomain(e.target.value);
  };
  const handlePrivacyUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrivacyUrl(e.target.value);
  };
  const handleEmailFooterChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmailFooter(e.target.value);
  };
  const handleAvatarNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAvatarName(e.target.value);
  };
  const handleSenderEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSenderEmail(e.target.value);
  };
  const handleSenderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSenderName(e.target.value);
  };
  const handleSupportEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupportEmail(e.target.value);
  };

  const handleSave = async () => {
    try {
      await upsertWhitelabelConfig({
        variables: {
          primaryColor,
          secondaryColor,
          successColor,
          warningColor,
          dangerColor,
          domain,
          emailFooter,
          avatarName,
          senderEmail,
          senderName,
          supportEmail,
          privacyUrl,
          klausName,
        },
      });
      enqueueSnackbar(t('route.whitelabel.saveSuccess', 'Whitelabel configuration saved successfully!'), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(t('route.whitelabel.saveError', 'Error saving whitelabel configuration.'), { variant: 'error' });
    }
  };

  return (
    <>
      {!hasWhitelabelPerms ? (
        <Redirect to="/dashboard" />
      ) : (
        <div className="flex flex-col items-center p-4 gap-8">
          {/* Color Configurations Section */}
          <div className="flex flex-col w-full max-w-lg gap-4 bg-white p-6 rounded-lg shadow-md">
            <h1 className="">{t('route.whitelabel.header', 'Whitelabel Configurator')}</h1>
            <p className="text-sm sm:text-base">
              {t(
                'route.whitelabel.description',
                'With this menu, you can configure your own custom branding for you company and any of your sub-tenants.'
              )}
            </p>

            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start gap-4 sm:gap-6">
                  <div className="flex flex-col sm:items-start">
                    <label className="font-semibold text-sm mb-2">
                      {t('route.whitelabel.primaryColor', 'Primary Color')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={handlePrimaryColorChange}
                        className="h-10 w-10 rounded-2xl border-6  border-gray-300 shadow-sm"
                      />
                      <span className="font-mono text-xs sm:text-sm">{primaryColor}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:items-start">
                    <label className="font-semibold text-sm mb-2">
                      {t('route.whitelabel.secondaryColor', 'Secondary Color')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={secondaryColor}
                        onChange={handleSecondaryColorChange}
                        className="h-10 w-10 rounded-full border border-gray-300 shadow-sm"
                      />
                      <span className="font-mono text-xs sm:text-sm">{secondaryColor}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:items-start">
                    <label className="font-semibold text-sm mb-2">
                      {t('route.whitelabel.successColor', 'Success Color')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={successColor}
                        onChange={handleSuccessColorChange}
                        className="h-10 w-10 rounded-full border border-gray-300 shadow-sm"
                      />
                      <span className="font-mono text-xs sm:text-sm">{successColor}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:items-start">
                    <label className="font-semibold text-sm mb-2">
                      {t('route.whitelabel.warningColor', 'Warning Color')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={warningColor}
                        onChange={handleWarningColorChange}
                        className="h-10 w-10 rounded-full border border-gray-300 shadow-sm"
                      />
                      <span className="font-mono text-xs sm:text-sm">{warningColor}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:items-start">
                    <label className="font-semibold text-sm mb-2">
                      {t('route.whitelabel.dangerColor', 'Danger Color')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={dangerColor}
                        onChange={handleDangerColorChange}
                        className="h-10 w-10 rounded-full border border-gray-300 shadow-sm"
                      />
                      <span className="font-mono text-xs sm:text-sm">{dangerColor}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:items-start w-full">
                  <label className="font-semibold text-sm mb-2">
                    {t('route.whitelabel.privacyUrl', 'Privacy URL')}
                  </label>
                  <input
                    type="text"
                    value={privacyUrl}
                    onChange={handlePrivacyUrlChange}
                    placeholder={t('route.whitelabel.privacyUrlPlaceholder', 'https://example.com/privacy')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      'route.whitelabel.privacyUrlHelp',
                      'Optional link to your privacy policy. Shown where legal links are displayed.'
                    )}
                  </p>
                </div>
                <div className="flex flex-col sm:items-start w-full">
                  <label className="font-semibold text-sm mb-2">
                    {t('route.whitelabel.avatarName', 'Avatar Name')}
                  </label>
                  <input
                    type="text"
                    value={avatarName}
                    onChange={handleAvatarNameChange}
                    placeholder={t('route.whitelabel.avatarNamePlaceholder', 'e.g. Dieter Bot')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('route.whitelabel.avatarNameHelp', 'Short display name for automated assistant / avatar.')}
                  </p>
                </div>
                <div className="flex flex-col sm:items-start w-full">
                  <label className="font-semibold text-sm mb-2">
                    {t('route.whitelabel.klausName', 'Name of the AI Act product (Klaus)')}
                  </label>
                  <input
                    type="text"
                    value={klausName}
                    onChange={(e) => setKlausName(e.target.value)}
                    placeholder={t('route.whitelabel.klausNamePlaceholder', 'e.g. AdvoAct')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      'route.whitelabel.klausNameHelp',
                      'Replaces "Klaus" wherever the app names the sibling AI Act product (links, merge offer, tasks).'
                    )}
                  </p>
                </div>
                <div className="flex flex-col sm:items-start w-full">
                  <label className="font-semibold text-sm mb-2">
                    {t('route.whitelabel.senderEmail', 'Sender Email')}
                  </label>
                  <input
                    type="email"
                    value={senderEmail}
                    onChange={handleSenderEmailChange}
                    placeholder={t('route.whitelabel.senderEmailPlaceholder', 'noreply@example.com')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      'route.whitelabel.senderEmailHelp',
                      'Optional custom From address for outgoing whitelabeled emails.'
                    )}
                  </p>
                </div>
                <div className="flex flex-col sm:items-start w-full">
                  <label className="font-semibold text-sm mb-2">
                    {t('route.whitelabel.senderName', 'Sender Name')}
                  </label>
                  <input
                    type="text"
                    value={senderName}
                    onChange={handleSenderNameChange}
                    placeholder={t('route.whitelabel.senderNamePlaceholder', 'e.g. Privacy Team')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      'route.whitelabel.senderNameHelp',
                      'Optional display name shown in the From header for outgoing emails.'
                    )}
                  </p>
                </div>
                <div className="flex flex-col sm:items-start w-full">
                  <label className="font-semibold text-sm mb-2">
                    {t('route.whitelabel.supportEmail', 'Support Email')}
                  </label>
                  <input
                    type="email"
                    value={supportEmail}
                    onChange={handleSupportEmailChange}
                    placeholder={t('route.whitelabel.supportEmailPlaceholder', 'support@example.com')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      'route.whitelabel.supportEmailHelp',
                      'Public-facing support email shown to users in the chatbot. If not set, falls back to the sender email.'
                    )}
                  </p>
                </div>
                <div className="flex flex-col sm:items-start w-full">
                  <label className="font-semibold text-sm mb-2">{t('route.whitelabel.domain', 'Domain')}</label>
                  <input
                    type="text"
                    value={domain}
                    onChange={handleDomainChange}
                    placeholder={t('route.whitelabel.domainPlaceholder', 'example.com')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      'route.whitelabel.domainHelp',
                      'Used for generating links and branding references (leave empty to skip custom domain).'
                    )}
                  </p>
                </div>
                <div className="flex flex-col sm:items-start w-full">
                  <label className="font-semibold text-sm mb-2">
                    {t('route.whitelabel.emailFooter', 'Email Footer')}
                  </label>
                  <textarea
                    value={emailFooter}
                    onChange={handleEmailFooterChange}
                    rows={4}
                    placeholder={t(
                      'route.whitelabel.emailFooterPlaceholder',
                      'Add an optional footer for outgoing emails (plain text or limited HTML).'
                    )}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      'route.whitelabel.emailFooterHelp',
                      'Keep it short; basic formatting tags (strong, em, a) may be supported.'
                    )}
                  </p>
                </div>
                <Button className="w-full sm:w-40 text-base " spinner={loading} onClick={handleSave} disabled={loading}>
                  {t('route.whitelabel.save-colors', 'Save Colors')}
                </Button>
              </div>
            </div>
          </div>

          {/* Logo Upload Section */}
          <div className="w-full max-w-lg bg-white p-6 rounded-lg shadow-md space-y-10">
            <div>
              <h2 className=" mb-4">{t('route.whitelabel.logoSection', 'Custom Logo')}</h2>
              <LogoUpload currentLogoB64={logoB64} variant="DEFAULT" previewBackground={primaryColor || '#ffffff'} />
            </div>
            <div>
              <h2 className=" mb-4">{t('route.whitelabel.lightLogoSection', 'Light Logo')}</h2>
              <p className="text-xs text-gray-500 mb-4">
                {t(
                  'route.whitelabel.lightLogoHelp',
                  'Optional alternate logo optimized for use on white backgrounds. If not set, the default logo is used.'
                )}
              </p>
              <LogoUpload currentLogoB64={lightLogoB64} variant="LIGHT" previewBackground="#ffffff" />
            </div>
            <div>
              <h2 className=" mb-4">{t('route.whitelabel.avatarSection', 'Custom Avatar')}</h2>
              <p className="text-xs text-gray-500 mb-4">
                {t(
                  'route.whitelabel.avatarHelp',
                  'Upload a custom avatar image to replace the default Dieter avatar throughout the application.'
                )}
              </p>
              <LogoUpload currentLogoB64={avatarB64} variant="AVATAR" previewBackground="#f5f5f5" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
